import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  Divider,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Description as ResumeIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import DashboardLayout from '../../layouts/DashboardLayout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const defaultForm = {
  phone_editable: false,
  pdf_fee_rupees: 20,
  docx_fee_rupees: 0,
  edit_fee_rupees: 10,
  resume_upi_id: '',
  pdf_minimalist_fee_rupees: 25,
  pdf_modern_fee_rupees: 30,
  pdf_creative_fee_rupees: 30,
};

const CareerResumeSettings = () => {
  const [form, setForm] = useState(defaultForm);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const authHeaders = () => {
    const token =
      localStorage.getItem('admin_token') ||
      localStorage.getItem('token') ||
      localStorage.getItem('user_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const applySettings = (s = {}) => {
    setForm({
      phone_editable: Boolean(s.phone_editable),
      pdf_fee_rupees: Number(s.pdf_fee_rupees) || 0,
      docx_fee_rupees: Number(s.docx_fee_rupees) || 0,
      edit_fee_rupees: Number(s.edit_fee_rupees) || 0,
      resume_upi_id: s.resume_upi_id || '',
      pdf_minimalist_fee_rupees:
        Number(s.pdf_minimalist_fee_rupees ?? s.pdf_compact_fee_rupees) || 0,
      pdf_modern_fee_rupees: Number(s.pdf_modern_fee_rupees) || 0,
      pdf_creative_fee_rupees:
        Number(s.pdf_creative_fee_rupees ?? s.pdf_executive_fee_rupees) || 0,
    });
    setTemplates(Array.isArray(s.templates) ? s.templates : []);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/career-profiles/settings`, {
        headers: authHeaders(),
      });
      if (res.data?.success && res.data.settings) {
        applySettings(res.data.settings);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Resume Controls - LOCSY';
    load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        phone_editable: Boolean(form.phone_editable),
        pdf_fee_rupees: Number(form.pdf_fee_rupees) || 0,
        docx_fee_rupees: Number(form.docx_fee_rupees) || 0,
        edit_fee_rupees: Number(form.edit_fee_rupees) || 0,
        resume_upi_id: String(form.resume_upi_id || '').trim(),
        pdf_minimalist_fee_rupees: Number(form.pdf_minimalist_fee_rupees) || 0,
        pdf_modern_fee_rupees: Number(form.pdf_modern_fee_rupees) || 0,
        pdf_creative_fee_rupees: Number(form.pdf_creative_fee_rupees) || 0,
      };
      const res = await axios.put(`${API_BASE_URL}/career-profiles/settings`, payload, {
        headers: authHeaders(),
      });
      if (res.data?.success) {
        toast.success('Resume controls saved');
        applySettings(res.data.settings || {});
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const feeField = (key, label, helper) => (
    <TextField
      fullWidth
      type="number"
      label={label}
      inputProps={{ min: 0, step: 1 }}
      value={form[key]}
      onChange={(e) => setForm((f) => ({ ...f, [key]: Number(e.target.value) }))}
      helperText={helper}
    />
  );

  return (
    <DashboardLayout>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <ResumeIcon color="primary" />
              <Typography variant="h5" fontWeight="bold">
                Resume Controls
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              1st-build PDF/Word prices per model · edit fee on later downloads · UPI for citizen app
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading || saving}>
              Refresh
            </Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={loading || saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Stack>
        </Stack>

        {loading ? (
          <Alert severity="info">Loading settings...</Alert>
        ) : (
          <Stack spacing={2}>
            <Alert severity="info">
              Resume files use career-profile data the customer asked to format
              (name, contact, education, work, skills, optional photo). Caste,
              Aadhaar and PAN are not collected on the career profile and are
              never printed on the PDF. Loksy does not send the file to employers
              unless the customer shares it. Job-alert caste for government
              matching is a separate form — do not mix it into Resume Controls.
            </Alert>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Career Profile Phone
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Citizen & user resume forms lock phone from login profile (discourages generating
                resumes for other numbers). Keep this off unless you have a special case.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(form.phone_editable)}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, phone_editable: e.target.checked }))
                    }
                  />
                }
                label={
                  form.phone_editable
                    ? 'Phone field is editable (not recommended)'
                    : 'Phone field is locked (login number only)'
                }
              />
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                1st build fees (create / first download)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Profile create/edit is free. These fees apply when the user generates PDF or Word
                the first time (or after a paid download with no further edits). ₹0 = free.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                {feeField('pdf_fee_rupees', 'Classic PDF (₹)', '2-page teal bars — no photo')}
                {feeField('pdf_minimalist_fee_rupees', 'Minimalist PDF (₹)', 'Novo-inspired · optional photo')}
                {feeField('pdf_modern_fee_rupees', 'Modern PDF (₹)', 'Sidebar · optional photo')}
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                {feeField('pdf_creative_fee_rupees', 'Creative PDF (₹)', 'Timeline · optional photo')}
                {feeField('docx_fee_rupees', 'Word / DOCX (₹)', 'Uses Classic layout')}
              </Stack>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Later edit fee
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                If the user changes profile fields after a paid PDF download, the next PDF charges
                model PDF fee + this edit fee. Profile form itself stays free (no paywall on Edit).
              </Typography>
              {feeField(
                'edit_fee_rupees',
                'Edit fee (₹)',
                'Added on later PDF after profile changes',
              )}
              <Divider sx={{ my: 2.5 }} />
              <TextField
                fullWidth
                label="Resume payment UPI ID (citizen app)"
                placeholder="yourname@upi"
                value={form.resume_upi_id}
                onChange={(e) => setForm((f) => ({ ...f, resume_upi_id: e.target.value }))}
                helperText="Used by Locsy Citizen when fee > ₹0 (React uses Razorpay)"
              />
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Templates
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {(templates.length
                  ? templates
                  : [
                      { id: 'classic', name: 'Classic', photo: false },
                      { id: 'minimalist', name: 'Minimalist', photo: true },
                      { id: 'modern', name: 'Modern', photo: true },
                      { id: 'creative', name: 'Creative', photo: true },
                    ]
                ).map((t) => (
                  <Chip
                    key={t.id}
                    label={`${t.name}${t.photo ? ' · photo' : ''}`}
                    color="primary"
                    variant="outlined"
                    title={t.description || t.name}
                  />
                ))}
              </Stack>
            </Paper>
          </Stack>
        )}
      </Container>
    </DashboardLayout>
  );
};

export default CareerResumeSettings;
